import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CommandPalette } from './CommandPalette';

export type SearchMode = { kind: 'app'; signOut: () => void } | { kind: 'public' };

interface SearchApi {
  open: boolean;
  show: (query?: string) => void;
  close: () => void;
}

const SearchContext = createContext<SearchApi | null>(null);

export function useSearch(): SearchApi {
  const value = useContext(SearchContext);
  if (!value) throw new Error('useSearch outside SearchProvider');
  return value;
}

export function SearchProvider({ mode, children }: { mode: SearchMode; children: ReactNode }) {
  const [state, setState] = useState({ open: false, query: '' });
  const show = useCallback((query = '') => setState({ open: true, query }), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing = (event.target as HTMLElement | null)?.closest('input, textarea, select, [contenteditable="true"]');
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setState((s) => ({ open: !s.open, query: '' }));
      } else if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setState({ open: true, query: '' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ open: state.open, show, close }), [state.open, show, close]);
  return (
    <SearchContext.Provider value={value}>
      {children}
      {state.open && <CommandPalette mode={mode} initialQuery={state.query} onClose={close} />}
    </SearchContext.Provider>
  );
}
